/**
 * Guarda un Blob en la carpeta de Descargas del usuario con un nombre dado.
 *
 * Nace de un bug real: al descargar el segundo documento de una sección, el
 * usuario abría el primero. La causa no era la petición —el blob que llegaba
 * siempre era el correcto— sino cómo se entregaba al navegador:
 *
 * 1. `URL.revokeObjectURL` corría en la MISMA vuelta que el `click()`. El
 *    navegador lee el blob de forma asíncrona, así que revocar de inmediato
 *    puede cancelar la descarga **sin ningún aviso**: no aparece archivo nuevo
 *    y el más reciente en la carpeta sigue siendo el anterior.
 * 2. El ancla nunca se conectaba al documento. En Chrome funciona, pero no está
 *    garantizado fuera de él.
 *
 * El `nombreArchivo` lo decide quien llama y **debe ser el mismo con el que la
 * pantalla identifica al documento**: si no coincide, dos documentos distintos
 * aterrizan con el mismo nombre, el navegador le agrega " (1)" al segundo, y el
 * usuario abre el que ya estaba — que fue justo el síntoma reportado.
 */
export function guardarBlobComo(blob: Blob, nombreArchivo: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const ancla = document.createElement('a')
  ancla.href = objectUrl
  ancla.download = nombreArchivo
  ancla.style.display = 'none'

  document.body.appendChild(ancla)
  ancla.click()
  document.body.removeChild(ancla)

  //* La revocación se difiere: mientras el navegador copia el blob a disco la URL
  //* tiene que seguir viva. Un minuto cubre de sobra el archivo más pesado que
  //* admite el expediente (100 MB) en un disco lento.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
}

/**
 * Nombre legible de un archivo a partir de la url de su blob.
 *
 * Le quita el prefijo `<epoch>-<8 hex>-` que ADR-0033 antepone al blob para que dos archivos
 * con el mismo nombre no se pisen en Azure. Ese token es un detalle de almacenamiento: cuando
 * se filtra a la pantalla el documento deja de parecer suyo
 * (`1787852517430-b77ea963-IDENTIFICACION.pdf`) y quien lo abre no reconoce lo que pidió.
 *
 * Es el ÚLTIMO recurso: si el archivo tiene `name` guardado, ese manda — el nombre del blob lo
 * puso quien generó el archivo (el banco bautiza el estado de cuenta con el mes de cierre, no
 * con el que cubre) y no tiene por qué coincidir con cómo se llama el documento en el expediente.
 */
export function nombreLegibleDeUrl(url: string): string {
  if (!url) return ''
  try {
    const base = url.substring(url.lastIndexOf('/') + 1)
    const q = base.indexOf('?')
    const sinQuery = decodeURIComponent(q >= 0 ? base.substring(0, q) : base)
    return sinQuery.replace(/^\d{13}-[0-9a-f]{8}-/, '')
  } catch {
    return url
  }
}

/**
 * Asegura que el nombre visible con el que se guarda un archivo conserve su
 * extensión, tomándola de la url del blob cuando el nombre no la traiga.
 *
 * Los documentos renombrados desde el panel suelen quedar sin extensión ("Acta
 * constitutiva"), y un archivo sin extensión no lo abre el sistema operativo.
 */
export function conExtensionDe(nombreVisible: string, url: string): string {
  const nombre = (nombreVisible || '').trim()
  if (!nombre) return ''
  if (/\.[a-z0-9]{2,5}$/i.test(nombre)) return nombre

  const ruta = (url || '').split('?')[0]
  const punto = ruta.lastIndexOf('.')
  const extension = punto > ruta.lastIndexOf('/') ? ruta.substring(punto) : ''
  return nombre + extension
}
